import type { ComponentProps } from "react";

import { cn } from "@/shared/lib/utils";

type GradientBackgroundProps = ComponentProps<"div"> & {
  /** Animate the blooms with a slow, subtle ambient drift (respects reduced-motion). */
  animated?: boolean;
};

/**
 * Decorative full-bleed background: two token-colored blooms that optionally
 * drift. Brand- and dark-mode-aware via design tokens; renders children above.
 */
export function GradientBackground({
  animated = false,
  className,
  children,
  ...props
}: GradientBackgroundProps) {
  return (
    <div
      {...props}
      data-animated={animated || undefined}
      className={cn("relative isolate bg-background", className)}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
      >
        <div
          className={cn(
            "absolute inset-0 bg-[radial-gradient(55%_50%_at_72%_12%,var(--schemes-primary-container),transparent_70%)] blur-2xl",
            animated && "motion-safe:animate-bloom-a",
          )}
        />
        <div
          className={cn(
            "absolute inset-0 bg-[radial-gradient(60%_55%_at_25%_80%,var(--schemes-secondary-container),transparent_70%)] blur-2xl",
            animated && "motion-safe:animate-bloom-b",
          )}
        />
      </div>
      {children}
    </div>
  );
}
